/**
 * L6 — SUPERADMIN_PLAN.md §6.
 *
 * Tenant-facing view of the SaaS billing surface. Every endpoint here is
 * mounted under /v1/my/* on the backend (see backend/internal/subscription)
 * and reads the org from the request context — never from URL/body — so
 * the frontend never needs to pass org_id.
 */

import { apiFetch } from '@/api/client'

export type LicenseKind = 'subscription' | 'perpetual' | 'internal'
export type DunningLevel = 'none' | 'past_due' | 'restricted' | 'suspended' | 'cancelled'
export type RestrictionLevel = 'none' | 'read_only' | 'suspended'
export type LicenseStatus =
  | 'trial'
  | 'active'
  | 'past_due'
  | 'restricted'
  | 'suspended'
  | 'cancelled'
export type BillingCycle = 'monthly' | 'annual'
export type PricingModel =
  | 'included'
  | 'flat_monthly'
  | 'per_user_monthly'
  | 'per_vehicle_monthly'
  | 'per_transaction'
  | 'commission'
export type PackCategory = 'core' | 'vertical' | 'addon'
export type PackStatus = 'trial' | 'active' | 'suspended' | 'cancelled'
export type SaaSInvoiceStatus = 'draft' | 'issued' | 'paid' | 'past_due' | 'cancelled'

export interface TenantLicense {
  org_id: string
  plan_id: string
  plan_code: string
  plan_name: string
  custom_max_users?: number
  custom_max_shipments_monthly?: number
  custom_max_storage_gb?: number
  negotiated_monthly_xof?: number
  started_at: string
  expires_at?: string | null
  auto_renew: boolean
  billing_cycle: BillingCycle
  status: LicenseStatus
  kind: LicenseKind
  dunning_exempt: boolean
  grace_period_days: number
  dunning_level: DunningLevel
  dunning_entered_at?: string | null
  dunning_frozen_until?: string | null
  dunning_notes?: string | null
  suspended_at?: string | null
  cancelled_at?: string | null
  cancellation_reason?: string | null
  notes?: string | null
  created_at: string
  updated_at: string
}

export interface TenantPack {
  id: string
  org_id: string
  pack_code: string
  pack_name: string
  pricing_model: PricingModel
  category: PackCategory
  status: PackStatus
  activated_at: string
  expires_at?: string | null
  suspended_at?: string | null
  cancelled_at?: string | null
  negotiated_base_price_xof?: number
  negotiated_per_unit_price_xof?: number
  negotiated_commission_pct?: number
  current_period_users?: number
  current_period_vehicles?: number
  current_period_transactions?: number
  current_period_amount_xof?: number
  notes?: string | null
  created_at: string
  updated_at: string
}

export interface SubscriptionInfo {
  license: TenantLicense
  packs: TenantPack[]
  modules: string[]
  workspaces: string[]
}

export interface SaaSInvoiceLine {
  pack_code: string
  description: string
  amount: number
}

export interface SaaSInvoice {
  id: string
  ref: string
  org_id: string
  org_name: string
  period_start: string
  period_end: string
  lines: SaaSInvoiceLine[]
  subtotal_xof: number
  tax_amount_xof: number
  total_xof: number
  currency: string
  status: SaaSInvoiceStatus
  issue_date?: string | null
  due_date?: string | null
  paid_date?: string | null
  pdf_url?: string | null
  notes?: string | null
  created_at: string
  updated_at: string
}

export interface UsageInfo {
  period: string
  active_users: number
  active_vehicles: number
  transactions_count: number
  transactions_amount_xof: number
}

interface Envelope<T> {
  data: T
}
interface ListEnvelope<T> {
  data: T[]
}

export const subscriptionApi = {
  get: () =>
    apiFetch<Envelope<SubscriptionInfo>>('/v1/my/subscription').then((r) => r.data),

  listInvoices: (status?: SaaSInvoiceStatus | 'all') => {
    const qs = status ? `?status=${status}` : ''
    return apiFetch<ListEnvelope<SaaSInvoice>>(`/v1/my/invoices${qs}`).then((r) => r.data ?? [])
  },

  getInvoice: (id: string) =>
    apiFetch<Envelope<SaaSInvoice>>(`/v1/my/invoices/${id}`).then((r) => r.data),

  invoicePDFUrl: (id: string) => `/v1/my/invoices/${id}/pdf`,

  usage: (period?: string) => {
    const qs = period ? `?period=${period}` : ''
    return apiFetch<Envelope<UsageInfo>>(`/v1/my/usage${qs}`).then((r) => r.data)
  },
}
